// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's zones (frontend-u0o.6, slice S6; mock 05): named
 * groups of rooms — a dwelling, a fire cell, a use (`GET /zones`, ReUseX
 * schema v43, ruxd #33). The zone ops of `POST /model/edits` (create from
 * the selected rooms, add / remove a member, name and use, delete), the
 * colour each zone wears on the plan (a `--type-N` swatch, only under the
 * Rum / Zone tools), its dashed outline (a padded convex hull of its rooms
 * on the plan's storey) and label, the browser's zone list with area and
 * volume, and the optimistic overlay of a zone batch. Pure: no React, no DOM.
 */

import type { ModelEditOp, SolidSpace, Zone } from '../api/types';
import { spaceLabel, type GeometriModel } from './geometri';
import type { XY } from './planWalls';
import { formatAreaDa, polygonArea } from './spaces';

/** The swatches zones and rooms are filled with (`--type-1` … `--type-11`). */
export const SWATCHES = 11;

/** A zone's swatch: by its row id, so it keeps its colour while others come and go. */
export function zoneSwatch(zone: Pick<Zone, 'id'>): string {
  return `--type-${((Math.max(1, zone.id) - 1) % SWATCHES) + 1}`;
}

/** A room's own swatch under the Rum tool (by row id), offset from the zones' so neighbours differ. */
export function roomSwatch(space: Pick<SolidSpace, 'id'>): string {
  return `--type-${((Math.max(1, space.id) + 4) % SWATCHES) + 1}`;
}

/** The zones a space is in (`SolidSpace.zones`, else the zones' member lists). */
export function zonesOf(zones: readonly Zone[] | null | undefined, guid: string): Zone[] {
  return (zones ?? []).filter((z) => z.spaces.includes(guid));
}

/** "Zone 1", "Zone 2", … the first number no zone's name uses. */
export function nextZoneName(zones: readonly Zone[] | null | undefined): string {
  const taken = new Set((zones ?? []).map((z) => z.name.trim().toLowerCase()));
  for (let n = 1; ; n += 1) if (!taken.has(`zone ${n}`)) return `Zone ${n}`;
}

/** A zone's name, or "Zone uden navn". */
export function zoneLabel(zone: Pick<Zone, 'name'>): string {
  return zone.name.trim() || 'Zone uden navn';
}

const roomCount = (n: number) => (n === 1 ? '1 rum' : `${n} rum`);

/** A volume in Danish: "78,3 m³" (one decimal below 1000 m³). */
export function formatVolumeDa(m3: number): string {
  const digits = m3 < 1000 ? 1 : 0;
  return `${m3.toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits })} m³`;
}

/** Area / volume of a zone from the model's rooms (the server's sums once read; the overlay's while a batch is on its way). */
export function zoneMeasures(model: GeometriModel, zone: Zone): { rooms: number; area: number; volume: number } {
  const byGuid = new Map((model.spaces ?? []).map((s) => [s.guid, s]));
  let area = 0;
  let volume = 0;
  let rooms = 0;
  for (const g of zone.spaces) {
    const s = byGuid.get(g);
    if (!s) continue;
    rooms += 1;
    const a = s.area ?? polygonArea(s.polygon);
    area += a;
    volume += s.volume ?? a * (s.z_ceiling - s.z_floor);
  }
  return { rooms, area, volume };
}

/** The plan's label of a zone: "Zone · Bolig A · 2 rum · 29,0 m²". */
export function zoneTag(model: GeometriModel, zone: Zone): string {
  const m = zoneMeasures(model, zone);
  const name = zoneLabel(zone);
  // "Zone 1" needs no "Zone ·" in front.
  return `${/^zone\b/i.test(name) ? name : `Zone · ${name}`} · ${roomCount(m.rooms)} · ${formatAreaDa(m.area)}`;
}

// --------------------------------------------------------- browser --

export interface ZoneRow {
  guid: string;
  name: string;
  use: string;
  swatch: string;
  /** "2 rum · 29,0 m² · 78,3 m³". */
  detail: string;
  rooms: number;
  area: number;
  volume: number;
  /** The member guids on the storey shown (all of them without a storey focus). */
  members: string[];
  /** Members on other storeys too. */
  elsewhere: boolean;
}

/**
 * The browser's Zoner list: every zone with a room on storey `storeyId`
 * (null: all), by row id, with its swatch and its totals over all its
 * rooms (a zone may span storeys: a maisonette).
 */
export function zoneRows(model: GeometriModel, storeyId: number | null): ZoneRow[] {
  const storeyOf = new Map((model.spaces ?? []).map((s) => [s.guid, s.storey_id]));
  const out: ZoneRow[] = [];
  for (const z of [...(model.zones ?? [])].sort((a, b) => a.id - b.id)) {
    const here = z.spaces.filter((g) => storeyOf.has(g) && (storeyId === null || storeyOf.get(g) === storeyId));
    // An empty zone shows on every storey (so it can be filled or deleted).
    if (storeyId !== null && here.length === 0 && z.spaces.some((g) => storeyOf.has(g))) continue;
    const m = zoneMeasures(model, z);
    out.push({
      guid: z.guid,
      name: zoneLabel(z),
      use: z.use,
      swatch: zoneSwatch(z),
      detail: `${roomCount(m.rooms)} · ${formatAreaDa(m.area)} · ${formatVolumeDa(m.volume)}`,
      rooms: m.rooms,
      area: m.area,
      volume: m.volume,
      members: here,
      elsewhere: here.length < z.spaces.filter((g) => storeyOf.has(g)).length,
    });
  }
  return out;
}

// -------------------------------------------------------------- ops --

export type ZoneResult = { ops: ModelEditOp[]; label: string } | { error: string };

const spacesIn = (model: GeometriModel, guids: readonly string[]) => {
  const byGuid = new Map((model.spaces ?? []).map((s) => [s.guid, s]));
  return guids.map((g) => byGuid.get(g)).filter((s): s is SolidSpace => s !== undefined);
};

/** A new zone of the selected rooms (other selected elements are left out). */
export function createZoneOps(model: GeometriModel, selection: readonly string[], guid: string, name?: string, use = ''): ZoneResult {
  const rooms = spacesIn(model, selection);
  if (rooms.length === 0) return { error: 'Vælg et eller flere rum for at samle dem i en zone' };
  const zname = (name ?? '').trim() || nextZoneName(model.zones);
  return {
    ops: [{ op: 'zone.create', record: { guid, name: zname, use: use.trim(), spaces: [...new Set(rooms.map((r) => r.guid))] } }],
    label: `Ny zone ${zname}`,
  };
}

const findZone = (model: GeometriModel, guid: string) => (model.zones ?? []).find((z) => z.guid === guid) ?? null;

/** Room `space` into zone `zone` when it is not a member, out of it when it is. */
export function toggleMemberOps(model: GeometriModel, zoneGuid: string, space: string): ZoneResult {
  const z = findZone(model, zoneGuid);
  const s = spacesIn(model, [space])[0];
  if (!z) return { error: 'Zonen findes ikke længere' };
  if (!s) return { error: 'Kun rum kan være med i en zone' };
  const member = z.spaces.includes(space);
  const spaces = member ? z.spaces.filter((g) => g !== space) : [...z.spaces, space];
  return {
    ops: [{ op: 'zone.update', guid: z.guid, set: { spaces } }],
    label: `${member ? 'Fjern' : 'Tilføj'} ${spaceLabel(s)} ${member ? 'fra' : 'til'} ${zoneLabel(z)}`,
  };
}

/**
 * Move room `space` to zone `target` (null: out of every zone). A room is
 * in one zone at a time here (the model allows more; the properties panel
 * moves it out of the others it is in).
 */
export function setRoomZoneOps(model: GeometriModel, space: string, target: string | null): ZoneResult | null {
  const s = spacesIn(model, [space])[0];
  if (!s) return { error: 'Kun rum kan være med i en zone' };
  const ops: ModelEditOp[] = [];
  for (const z of model.zones ?? []) {
    const member = z.spaces.includes(space);
    if (z.guid === target && !member) ops.push({ op: 'zone.update', guid: z.guid, set: { spaces: [...z.spaces, space] } });
    else if (z.guid !== target && member) ops.push({ op: 'zone.update', guid: z.guid, set: { spaces: z.spaces.filter((g) => g !== space) } });
  }
  if (ops.length === 0) return null;
  const tz = target ? findZone(model, target) : null;
  return { ops, label: tz ? `${spaceLabel(s)} til ${zoneLabel(tz)}` : `${spaceLabel(s)} ud af zonen` };
}

/** Name or use of a zone; null when nothing changes (or a name is left empty). */
export function zoneFieldOps(model: GeometriModel, zoneGuid: string, field: 'name' | 'use', value: string): ZoneResult | null {
  const z = findZone(model, zoneGuid);
  if (!z) return null;
  const v = value.trim();
  if (field === 'name' && v === '') return null;
  if (v === (field === 'name' ? z.name : z.use)) return null;
  return { ops: [{ op: 'zone.update', guid: z.guid, set: { [field]: v } }], label: `${field === 'name' ? 'Omdøb' : 'Anvendelse'} · ${zoneLabel(z)}` };
}

/** Slet zone: the rooms stay. */
export function deleteZoneOps(model: GeometriModel, zoneGuid: string): ZoneResult {
  const z = findZone(model, zoneGuid);
  if (!z) return { error: 'Zonen findes ikke længere' };
  return { ops: [{ op: 'zone.delete', guid: z.guid }], label: `Slet zone ${zoneLabel(z)}` };
}

// ---------------------------------------------------------- overlay --

/**
 * The zones as `ops` leave them (the optimistic overlay, `applyOps`): a
 * create added as sent, an update's name / use / spaces, a delete; a
 * `space.delete` drops the room from every zone, as the server does. The
 * sums are recomputed from `spaces` (the model's rooms after the batch).
 */
export function applyZoneOps(zones: readonly Zone[], ops: readonly ModelEditOp[], spaces: readonly SolidSpace[] | null): Zone[] {
  let out = [...zones];
  for (const op of ops) {
    if (op.op === 'zone.create' && 'record' in op) {
      const rec = op.record as Partial<Zone>;
      if (typeof rec.guid !== 'string') continue;
      const id = Math.max(0, ...out.map((z) => z.id)) + 1;
      out = [
        ...out.filter((z) => z.guid !== rec.guid),
        {
          id,
          guid: rec.guid,
          name: typeof rec.name === 'string' ? rec.name : '',
          use: typeof rec.use === 'string' ? rec.use : '',
          spaces: Array.isArray(rec.spaces) ? [...rec.spaces] : [],
          storey_ids: [],
          area: 0,
          volume: 0,
          missing: [],
          source: 'manual',
          locked: true,
          edited: true,
          confidence: null,
          metadata: null,
        },
      ];
    } else if (op.op === 'zone.update' && 'set' in op) {
      const set = op.set as Partial<Zone>;
      out = out.map((z) =>
        z.guid === op.guid
          ? {
              ...z,
              ...(typeof set.name === 'string' ? { name: set.name } : {}),
              ...(typeof set.use === 'string' ? { use: set.use } : {}),
              ...(Array.isArray(set.spaces) ? { spaces: [...set.spaces] } : {}),
              edited: true,
            }
          : z,
      );
    } else if (op.op === 'zone.delete' && 'guid' in op) {
      out = out.filter((z) => z.guid !== op.guid);
    } else if (op.op === 'space.delete' && 'guid' in op) {
      const g = op.guid;
      out = out.map((z) => (z.spaces.includes(g) ? { ...z, spaces: z.spaces.filter((x) => x !== g) } : z));
    }
  }
  // The sums follow the members.
  const byGuid = new Map((spaces ?? []).map((s) => [s.guid, s]));
  return out.map((z) => {
    let area = 0;
    let volume = 0;
    const storeys = new Set<number>();
    for (const g of z.spaces) {
      const s = byGuid.get(g);
      if (!s) continue;
      const a = s.area ?? polygonArea(s.polygon);
      area += a;
      volume += s.volume ?? a * (s.z_ceiling - s.z_floor);
      if (s.storey_id !== null) storeys.add(s.storey_id);
    }
    return { ...z, area, volume, storey_ids: [...storeys].sort((a, b) => a - b) };
  });
}

// ---------------------------------------------------------- outline --

/** Douglas–Peucker on an open polyline. */
function simplifyLine(pts: readonly XY[], tol: number): XY[] {
  if (pts.length <= 2) return [...pts];
  const [a, b] = [pts[0], pts[pts.length - 1]];
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const L = Math.hypot(dx, dy);
  let worst = -1;
  let at = 0;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const p = pts[i];
    const d = L > 1e-12 ? Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / L : Math.hypot(p[0] - a[0], p[1] - a[1]);
    if (d > worst) {
      worst = d;
      at = i;
    }
  }
  if (worst <= tol) return [a, b];
  return [...simplifyLine(pts.slice(0, at + 1), tol).slice(0, -1), ...simplifyLine(pts.slice(at), tol)];
}

/** A closed ring simplified (split at its two farthest-apart corners so both halves keep their ends). */
function simplifyRing(ring: readonly XY[], tol: number): XY[] {
  if (ring.length <= 4) return [...ring];
  let far = 0;
  let best = -1;
  for (let i = 1; i < ring.length; i += 1) {
    const d = Math.hypot(ring[i][0] - ring[0][0], ring[i][1] - ring[0][1]);
    if (d > best) {
      best = d;
      far = i;
    }
  }
  const one = simplifyLine(ring.slice(0, far + 1), tol);
  const two = simplifyLine([...ring.slice(far), ring[0]], tol);
  return [...one.slice(0, -1), ...two.slice(0, -1)];
}

const inRing = (x: number, y: number, ring: readonly XY[]) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/**
 * The outline of rooms grown `pad` metres each way (square to the
 * building's axes at `angle`), so rooms a wall apart become one region:
 * its boundary loops, world, the outer ones counter-clockwise. Rasterised
 * at `cell` metres in the axes' frame and traced (marching squares), then
 * simplified — an L-shaped dwelling is an L, not a hull over its
 * neighbour.
 */
export function unionOutline(rings: readonly (readonly XY[])[], pad: number, angle = 0, cell = 0.05): XY[][] {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const toLocal = (p: XY): XY => [p[0] * c + p[1] * s, -p[0] * s + p[1] * c];
  const toWorld = (p: XY): XY => [p[0] * c - p[1] * s, p[0] * s + p[1] * c];
  const local = rings.filter((r) => r.length >= 3).map((r) => r.map(toLocal));
  if (local.length === 0) return [];
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const r of local) {
    for (const p of r) {
      x0 = Math.min(x0, p[0]);
      y0 = Math.min(y0, p[1]);
      x1 = Math.max(x1, p[0]);
      y1 = Math.max(y1, p[1]);
    }
  }
  // Keep the grid bounded (a zone spread over a big building gets a coarser cell).
  const span = Math.max(x1 - x0, y1 - y0) + 2 * pad;
  const h = Math.max(cell, span / 600);
  const ox = x0 - pad - h;
  const oy = y0 - pad - h;
  const nx = Math.ceil((x1 - x0 + 2 * pad) / h) + 3;
  const ny = Math.ceil((y1 - y0 + 2 * pad) / h) + 3;
  const grid = new Uint8Array(nx * ny);
  // A cell is in when its centre, or a point `pad` away from it along an axis or a diagonal, is in a room.
  const offs: XY[] = [[0, 0], [pad, 0], [-pad, 0], [0, pad], [0, -pad], [pad, pad], [pad, -pad], [-pad, pad], [-pad, -pad]];
  for (let j = 0; j < ny; j += 1) {
    for (let i = 0; i < nx; i += 1) {
      const x = ox + (i + 0.5) * h;
      const y = oy + (j + 0.5) * h;
      if (offs.some((o) => local.some((r) => inRing(x + o[0], y + o[1], r)))) grid[j * nx + i] = 1;
    }
  }
  const on = (i: number, j: number) => i >= 0 && j >= 0 && i < nx && j < ny && grid[j * nx + i] === 1;
  // Boundary edges between grid corners, inside on the left: a → b.
  const next = new Map<number, number[]>();
  const key = (i: number, j: number) => j * (nx + 1) + i;
  const add = (a: number, b: number) => {
    const list = next.get(a);
    if (list) list.push(b);
    else next.set(a, [b]);
  };
  for (let j = 0; j < ny; j += 1) {
    for (let i = 0; i < nx; i += 1) {
      if (!on(i, j)) continue;
      if (!on(i, j - 1)) add(key(i, j), key(i + 1, j));
      if (!on(i + 1, j)) add(key(i + 1, j), key(i + 1, j + 1));
      if (!on(i, j + 1)) add(key(i + 1, j + 1), key(i, j + 1));
      if (!on(i - 1, j)) add(key(i, j + 1), key(i, j));
    }
  }
  const loops: XY[][] = [];
  for (const [startKey] of next) {
    while ((next.get(startKey)?.length ?? 0) > 0) {
      const loop: number[] = [startKey];
      let at = next.get(startKey)!.pop()!;
      while (at !== startKey) {
        loop.push(at);
        const list = next.get(at);
        if (!list || list.length === 0) break;
        at = list.pop()!;
      }
      const pts = loop.map((k) => [ox + (k % (nx + 1)) * h, oy + Math.floor(k / (nx + 1)) * h] as XY);
      const simple = simplifyRing(pts, h * 0.75);
      if (simple.length >= 3) loops.push(simple.map(toWorld));
    }
  }
  return loops;
}

export interface ZoneOutline {
  guid: string;
  name: string;
  swatch: string;
  tag: string;
  /** World loops (one per separate group of its rooms; holes too). */
  rings: XY[][];
}

/**
 * The dashed outlines of the zones with rooms on storey `storeyId`: the
 * union of those rooms grown `pad` metres (over the walls between and
 * round them), square to the storey's axes at `angle`.
 */
export function zoneOutlines(model: GeometriModel, storeyId: number | null, pad = 0.15, angle = 0): ZoneOutline[] {
  const byGuid = new Map((model.spaces ?? []).map((s) => [s.guid, s]));
  const out: ZoneOutline[] = [];
  for (const z of model.zones ?? []) {
    const rings: XY[][] = [];
    for (const g of z.spaces) {
      const s = byGuid.get(g);
      if (!s || (storeyId !== null && s.storey_id !== storeyId)) continue;
      rings.push(s.polygon.outer.map((p) => [p[0], p[1]] as XY));
    }
    const loops = unionOutline(rings, pad, angle);
    if (loops.length === 0) continue;
    out.push({ guid: z.guid, name: zoneLabel(z), swatch: zoneSwatch(z), tag: zoneTag(model, z), rings: loops });
  }
  return out;
}

/**
 * The plan's room fills under the Rum and Zone tools (guid → swatch token):
 * a zone's rooms wear the zone's colour; under Rum every other room its
 * own, under Zone the rest stay unfilled (only membership is coloured).
 */
export function roomFills(model: GeometriModel, tool: 'room' | 'zone', pending: readonly string[] = []): Map<string, string> {
  const out = new Map<string, string>();
  if (tool === 'room') for (const s of model.spaces ?? []) out.set(s.guid, roomSwatch(s));
  for (const z of [...(model.zones ?? [])].sort((a, b) => b.id - a.id)) for (const g of z.spaces) out.set(g, zoneSwatch(z));
  // The Zone tool's picks for a new zone: the swatch it will get.
  if (pending.length > 0) {
    const next = `--type-${(Math.max(0, ...(model.zones ?? []).map((z) => z.id)) % SWATCHES) + 1}`;
    for (const g of pending) out.set(g, next);
  }
  return out;
}

// ------------------------------------------------- properties panel --

export interface RoomZoneView {
  /** The zones to pick from (by id), with their swatch. */
  options: { guid: string; name: string; swatch: string }[];
  /** The zone the room is in (the first, when the model has it in more), or null. */
  zone: { guid: string; name: string; use: string; swatch: string; area: string; volume: string; rooms: { guid: string; label: string }[] } | null;
  /** Its other zones' names, when it is in more than one. */
  others: string[];
}

/** Egenskaber's Zone section for room `guid`: the zone it is in, its rooms and totals, and the zones to move it to. */
export function roomZoneView(model: GeometriModel, guid: string): RoomZoneView | null {
  if (!model.zones || !(model.spaces ?? []).some((s) => s.guid === guid)) return null;
  const byGuid = new Map((model.spaces ?? []).map((s) => [s.guid, s]));
  const sorted = [...model.zones].sort((a, b) => a.id - b.id);
  const mine = sorted.filter((z) => z.spaces.includes(guid));
  const z = mine[0] ?? null;
  const m = z ? zoneMeasures(model, z) : null;
  return {
    options: sorted.map((x) => ({ guid: x.guid, name: zoneLabel(x), swatch: zoneSwatch(x) })),
    zone:
      z && m
        ? {
            guid: z.guid,
            name: z.name,
            use: z.use,
            swatch: zoneSwatch(z),
            area: formatAreaDa(m.area),
            volume: formatVolumeDa(m.volume),
            rooms: z.spaces.flatMap((g) => {
              const s = byGuid.get(g);
              return s ? [{ guid: g, label: spaceLabel(s) }] : [];
            }),
          }
        : null,
    others: mine.slice(1).map(zoneLabel),
  };
}
